import { CloseOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useDmStore } from '../stores';
import ChannelView from './ChannelView';
import styles from './DirectMessagePanel.module.css';
import { UserAvatar } from './UserAvatar';

/** Right-side panel for a direct-message conversation opened from a detail surface. */
export default function DirectMessagePanel({
  channelId,
  onClose,
}: {
  channelId: string;
  onClose: () => void;
}) {
  const directMessage = useDmStore((state) =>
    state.dmChannels.find((item) => item.id === channelId),
  );
  const loadDmChannels = useDmStore((state) => state.loadDmChannels);
  const [hydrating, setHydrating] = useState(!directMessage);

  useEffect(() => {
    if (directMessage) {
      setHydrating(false);
      return;
    }
    let active = true;
    loadDmChannels().finally(() => {
      if (active) setHydrating(false);
    });
    return () => {
      active = false;
    };
  }, [channelId, directMessage, loadDmChannels]);

  const peer = directMessage?.peer;
  return (
    <section className={styles.root} aria-label="Direct message">
      <header>
        {peer ? (
          <>
            <span className={styles.avatar}>
              <UserAvatar
                userId={peer.id}
                displayName={peer.display_name}
                avatarUrl={peer.avatar_url}
                role={peer.role}
                state={peer.state}
                size={28}
              />
            </span>
            <span className={styles.heading}>
              <strong>{peer.display_name}</strong>
              <small>{peer.role === 'agent' ? 'Agent' : 'Direct message'}</small>
            </span>
          </>
        ) : (
          <span>Direct message</span>
        )}
        <button type="button" aria-label="Close direct message" onClick={onClose}>
          <CloseOutlined />
        </button>
      </header>
      {hydrating ? (
        <div className={styles.loading}>Loading conversation…</div>
      ) : (
        <ChannelView channelId={channelId} hideHeader />
      )}
    </section>
  );
}
